import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../../shared/interface/user-permissions";
import {delay, distinctUntilChanged, first, takeUntil, tap} from "rxjs/operators";
import {select, Store} from "@ngrx/store";
import {getCurrentRouteState} from "../../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../../shared/ngrx/route-serializer";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AuthService} from "../../../../shared/services/auth.service";
import {ActivatedRoute, Router} from "@angular/router";
import {FilterService} from "../../../../shared/services/filter.service";
import {StoreRootState} from "../../../../shared/ngrx/reducers";
import {NgForm} from "@angular/forms";
import {PostService} from "../../../../shared/services/post.service";
import {CountedAndPaginated} from "../../../../shared/interface/response/counted-and-paginated";
import {Contract} from "../../../../shared/interface/contract";
import {Post} from "../../../../shared/interface/post";
import {ConfirmDialogComponent} from "../../../../shared/components/confirm-dialog/confirm-dialog.component";
import {POST_TAG_TYPE_POST_TYPE} from "../../../../shared/services/dict.service";
import {UserService} from "../../../../shared/services/user.service";
import {User} from "../../../../shared/interface/user";
import {Profile} from "../../../../shared/interface/profile";
import {HttpErrorResponse} from "@angular/common/http";
import {Uuid} from "../../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../../shared/components/job-result-dialog/job-result-dialog.component";

@Component({
  selector: 'app-all-post-list',
  templateUrl: './all-post-list.component.html',
  styleUrls: ['./all-post-list.component.scss']
})
export class AllPostListComponent implements OnInit, AfterViewInit, OnDestroy  {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('filtersForm') filtersForm: NgForm;

  initialFilters: any;
  totalData = 0;
  posts: Post[] = [];
  userPermissions: ReplaySubject<UserPermissions>;
  profile: Profile;
  displayedColumns: string[] = ['name', 'type',  'user', 'createAt', 'startAt', 'endAt', 'status', 'actions'];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  readonly POST_TAG_TYPE_POST_TYPE = POST_TAG_TYPE_POST_TYPE;

  constructor(
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
    private postService: PostService,
    private userService: UserService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.auth.getProfile()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((profile: Profile) => {
        this.profile = profile;
      });
    this.postService.getPostListSubject()
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<Post>) => {
          this.posts = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      contains$postName: 'string',
      type: 'int',
      username: 'string',
      gte$createdAt: 'date',
      lte$createdAt: 'date',
      gte$startAt: 'date',
      lte$startAt: 'date',
      gte$endAt: 'date',
      lte$endAt: 'date',
      permissionTag: 'int[]',
      competitionCta: 'int',
      structure: 'string[]'
    }, filters);
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        ...this.filtersForm.value
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.loadListData();
      })
    ;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    return filters;
  }

  loadListData(): void {
    this.postService.loadList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  add(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  delete(postId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć post?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.postService.delete(postId)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto post', '', {duration: 2000});
            this.loadListData();
          });
      }
    });
  }

  report(post: Post): void {
    const filters = {...this.initialFilters};

    filters.permissionOfwcaCompetition = post.permissionOfwcaCompetition;
    filters.permissionOfwcaProduct = post.permissionOfwcaProduct;
    filters.permissionOfwca = post.permissionOfwca;
    filters.permissionPartner = post.permissionPartner;
    filters.permissionPartnerCompetition = post.permissionPartnerCompetition;
    if (null !== post.permissionContractType) {
      filters.permissionContractType = post.permissionContractType.toString();
    }
    if (null !== post.permissionTag) {
    filters.permissionTag = post.permissionTag.toString();
    }
    if (null !== post.permissionIddTrainingStatus) {
    filters.permissionIddTrainingStatus = post.permissionIddTrainingStatus.toString();
    }
    if (null !== post.permissionTagPartner) {
    filters.permissionTagPartner = post.permissionTagPartner.toString();
    }
    if (null !== post.permissionRegional) {
    filters.permissionRegional = post.permissionRegional.toString();
    }

    this.userService.downloadAccessReport(filters);
  }

  copyPost(post: Post): void {
    this.postService.copy(post.uuid)
      .subscribe(() => {
          this.snack.open('Pomyślnie skopiowano post', '', {duration: 2000});
          this.loadListData();
          }
        , (error: HttpErrorResponse) => {
          this.snack.open('Nie udało się skopiować_postu ', error.status.toString(), {
            duration: 2000
          });
        });
  }

  downloadReport(): void {
    this.postService.downloadReport(this.initialFilters)
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false, asf360: true },
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }


  downloadReportCTA(uuid): void {
    const filters = {...this.initialFilters};

    filters.uuid =  uuid;

    this.postService.downloadReportCta(filters);
  }

}
